import type { PurchaseOrderListItem } from '../../../services/purchaseOrders';

// Vendor cost is per line and often missing, so a PO total is only ever a sum
// over the costed lines - these helpers keep that partiality visible.

const CENTS_PER_DOLLAR = 100;

// Below this, an expected-vs-invoiced difference is rounding, not a variance.
export const VARIANCE_TOLERANCE_CENTS = 100;

export type ExpectedCostSource = Pick<
    PurchaseOrderListItem,
    'expectedTotalCents' | 'linesAwaitingCost' | 'lineCount'
>;

export interface ExpectedCostSummary {
    /** Integer cents over the costed lines, or null when none carry a cost. */
    totalCents: number | null;
    costedLines: number;
    totalLines: number;
    linesAwaitingCost: number;
    /** Every line carries a cost, so the total is the real total. */
    complete: boolean;
}

export function expectedCostSummary(po: ExpectedCostSource): ExpectedCostSummary {
    const totalLines = Math.max(po.lineCount, 0);
    const awaiting = Math.min(Math.max(po.linesAwaitingCost, 0), totalLines);
    const costedLines = totalLines - awaiting;
    return {
        totalCents: costedLines > 0 ? po.expectedTotalCents : null,
        costedLines,
        totalLines,
        linesAwaitingCost: awaiting,
        complete: totalLines > 0 && awaiting === 0
    };
}

/** "2 of 3 lines costed" - null once every line carries a cost. */
export function expectedCostQualifier(summary: ExpectedCostSummary): string | null {
    if (summary.complete || summary.totalLines === 0) return null;
    if (summary.costedLines === 0) return 'no vendor cost on file';
    return `${summary.costedLines} of ${summary.totalLines} lines costed`;
}

export function dollarStringToCents(value: string): number {
    return Math.round(Number(value) * CENTS_PER_DOLLAR);
}

export type VendorInvoiceVariance =
    /** No vendor invoice recorded yet. */
    | { kind: 'not_invoiced' }
    /** Invoiced, but the expected side is missing or partial - no comparison. */
    | { kind: 'incomparable'; invoicedCents: number }
    | { kind: 'known'; invoicedCents: number; varianceCents: number; withinTolerance: boolean };

export type VarianceSource = ExpectedCostSource &
    Pick<PurchaseOrderListItem, 'vendorInvoicedAmount'>;

// Positive variance = the vendor billed more than expected.
export function vendorInvoiceVariance(po: VarianceSource): VendorInvoiceVariance {
    if (po.vendorInvoicedAmount === null) return { kind: 'not_invoiced' };
    const invoicedCents = dollarStringToCents(po.vendorInvoicedAmount);
    if (!Number.isFinite(invoicedCents)) return { kind: 'not_invoiced' };

    const summary = expectedCostSummary(po);
    if (!summary.complete || summary.totalCents === null) {
        return { kind: 'incomparable', invoicedCents };
    }
    const varianceCents = invoicedCents - summary.totalCents;
    return {
        kind: 'known',
        invoicedCents,
        varianceCents,
        withinTolerance: Math.abs(varianceCents) < VARIANCE_TOLERANCE_CENTS
    };
}
